// Libraries
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

// MUI
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

// Core
import { ResourceTextApplication } from '../../../core/resources';

// Common
import { ADialog, ADialogActions, ADialogContent, ADialogTitle } from '../../../common/dialog';
import {
    IBatchInfo,
    IBovineInfo,
    ICollectWeek,
    ICreateAnnouncement,
    ICreatePreliminaryPurchaseAnnouncement,
    IUpdateAnnouncementResult,
} from '../../../interfaces/IAnnouncement';
import animalAnnouncementApi from '../../../api/animalAnnouncementApi';
import { GUIDType } from '../../../interfaces/types';
import { AnnouncementTypeEnum, BovineSex, SalesType } from '../../../interfaces/enums';
import { defaultSalesType } from '../../announcements/helpers/salesType';

export interface ICreatePreliminaryPurchaseAnnouncementDialog {
    onClose: (newAnnouncement?: IUpdateAnnouncementResult) => any;
    preliminaryAnnouncementData: ICreateAnnouncement;
    linkedAnnouncementId: GUIDType;
    linkedAnnouncementType: AnnouncementTypeEnum;
    collectWeekStart: ICollectWeek;
    collectWeekEnd: ICollectWeek;
}

export default function CreatePreliminaryPurchaseAnnouncementDialog(
    props: ICreatePreliminaryPurchaseAnnouncementDialog
) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaBovine', 'AnelmaGeneral']);
    const { collectWeekStart, collectWeekEnd } = props;
    const [loading, setLoading] = useState<boolean>(false);

    const savePurchaseAnnouncement = (
        preliminaryAnnouncementData: ICreateAnnouncement,
        announcementId: GUIDType
    ) => {
        setLoading(true);
        let bovine = preliminaryAnnouncementData.BovineInfos[0];

        const updatedBovineInfos: IBatchInfo = {
            HoldingSiteId: bovine.HoldingSiteId,
            SalesType: defaultSalesType(AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement, bovine.Sex),
            AnimalCount: preliminaryAnnouncementData.BovineInfos.length,
            CollectWeekStart: {
                Year: collectWeekStart.Year,
                Week: collectWeekStart.Week,
            },
            CollectWeekEnd: {
                Year: collectWeekEnd.Year,
                Week: collectWeekEnd.Week,
            },
            CollectWeekDay: 3,
            AddInfo: '',
        };

        const announcementsToSave: ICreatePreliminaryPurchaseAnnouncement = {
            ...preliminaryAnnouncementData,
            BovineInfos: [updatedBovineInfos],
            MainAnnouncementGuid: announcementId,
        };

        animalAnnouncementApi
            .createPreliminaryBovinePurchaseAnnouncement(announcementsToSave)
            .then(
                (response) => {
                    setLoading(false);

                    if (response)
                        props.onClose(response.Entity);
                    else
                        props.onClose();
                },
                () => {
                    setLoading(false);
                }
            )
            .finally(() => setLoading(false));
    };

    const getText = (announcementType: AnnouncementTypeEnum) => {
        let res = '';
        let collectWeek = '';

        if (props.preliminaryAnnouncementData
            && props.preliminaryAnnouncementData.BovineInfos
            && props.preliminaryAnnouncementData.BovineInfos.length > 0
            && props.preliminaryAnnouncementData.BovineInfos[0].CollectWeekStart)
            collectWeek = props.preliminaryAnnouncementData.BovineInfos[0].CollectWeekStart.Week.toString()
                + "/" + props.preliminaryAnnouncementData.BovineInfos[0].CollectWeekStart.Year.toString();

        if (announcementType == AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) {
            res = t(`AnelmaBovine:1203`).replace('{0}', collectWeek);
        } else if (announcementType == AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) {
            res = t(`AnelmaBovine:1204`).replace('{0}', collectWeek);
        }

        return res;
    };

    return (
        <ADialog onClose={() => props.onClose()} open={true}>
            <ADialogTitle>{t(`AnelmaBovine:1200`)}</ADialogTitle>

            <ADialogContent size='sm'>
                <Grid container style={{ paddingLeft: '24px' }}>
                    <Typography variant='body1' style={{ paddingBottom: '10px' }}>
                        {getText(props.linkedAnnouncementType)}
                    </Typography>

                    <Typography variant='body1' style={{ paddingBottom: '10px' }}>
                        {t('AnelmaBovine:1205')}
                    </Typography>
                </Grid>
            </ADialogContent>

            <ADialogActions
                buttons={{
                    left: [
                        {
                            onClick: () => props.onClose(),
                            type: 'cancel',
                            label: t(`AnelmaBovine:1206`),
                            disabled: loading,
                        },
                    ],
                    right: [
                        {
                            onClick: () =>
                                savePurchaseAnnouncement(
                                    props.preliminaryAnnouncementData,
                                    props.linkedAnnouncementId
                                ),
                            type: 'save',
                            label: t(`AnelmaBovine:1207`),
                            disabled: loading,
                        },
                    ],
                }}
            />
        </ADialog>
    );
}
